import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Switch,
  FormControlLabel,
  Divider,
  Collapse,
  Button,
  Grid,
  Dialog,
  Zoom,
} from '@mui/material';
import {
  CheckCircle,
  ErrorOutline,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { DogChecklistIcon, RusticFarmIcon } from './icons/ChecklistWaivedIcons';
import { Documento } from '../services/documentoService';
import {
  buildChecklistItems,
  CHECKLIST_GROUPS,
  ChecklistItem,
  documentChecklistStorageKeys,
  readHuntWithDogsPreference,
} from '../utils/documentChecklist';
import { branding } from '../config/branding';

type DocumentChecklistProps = {
  documentos: Documento[];
  onItemClick: (item: ChecklistItem) => void;
  embedded?: boolean;
};

const DocumentChecklist: React.FC<DocumentChecklistProps> = ({
  documentos,
  onItemClick,
  embedded = false,
}) => {
  const [isOwner, setIsOwner] = useState(true);
  const [huntWithDogs, setHuntWithDogs] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [zoomedWaivedIcon, setZoomedWaivedIcon] = useState<'dog' | 'farm' | null>(null);

  useEffect(() => {
    const storedOwner = localStorage.getItem(documentChecklistStorageKeys.isOwner);
    if (storedOwner !== null) {
      setIsOwner(storedOwner === 'true');
    }
    const storedWithDogs = readHuntWithDogsPreference();
    if (storedWithDogs !== null) {
      setHuntWithDogs(storedWithDogs);
    }
  }, []);

  const handleOwnerChange = (value: boolean) => {
    setIsOwner(value);
    localStorage.setItem(documentChecklistStorageKeys.isOwner, value ? 'true' : 'false');
  };

  const handleHuntWithDogsChange = (value: boolean) => {
    setHuntWithDogs(value);
    localStorage.setItem(documentChecklistStorageKeys.withDogs, value ? 'true' : 'false');
  };

  const checklistItems = useMemo(
    () => buildChecklistItems(documentos, isOwner, huntWithDogs),
    [documentos, isOwner, huntWithDogs]
  );

  const itemsByKey = useMemo(
    () => Object.fromEntries(checklistItems.map((item) => [item.key, item])),
    [checklistItems]
  );

  const checklistOk = checklistItems.every((item) => item.ok);
  const checklistDone = checklistItems.filter((item) => item.ok).length;
  const checklistTotal = checklistItems.length;
  const checklistProgress = checklistTotal > 0 ? (checklistDone / checklistTotal) * 100 : 0;

  useEffect(() => {
    if (!checklistOk) {
      setDetailsOpen(true);
    }
  }, [checklistOk]);

  const renderItemIcon = (item: ChecklistItem, large = false) => {
    const waivedSize = large ? 56 : 52;
    if (item.waived) {
      return item.waivedIcon === 'dog' ? (
        <DogChecklistIcon size={waivedSize} />
      ) : (
        <RusticFarmIcon size={waivedSize} />
      );
    }
    if (item.ok) {
      return <CheckCircle sx={{ fontSize: large ? 22 : 18, color: '#2e7d32' }} />;
    }
    return <ErrorOutline sx={{ fontSize: large ? 22 : 18, color: '#ef6c00' }} />;
  };

  const renderChecklistItem = (item: ChecklistItem) => {
    const isActionable = !item.waived;
    return (
      <Box
        key={item.key}
        onClick={() => isActionable && onItemClick(item)}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: item.waived ? '#e0e0e0' : item.ok ? '#c8e6c9' : '#ffe0b2',
          bgcolor: item.waived ? '#fafafa' : item.ok ? '#f1f8f4' : '#fffaf3',
          cursor: isActionable ? 'pointer' : 'default',
          transition: 'background-color 0.15s',
          height: '100%',
          '&:hover': isActionable
            ? { bgcolor: item.ok ? '#e8f5e9' : '#fff3e0' }
            : undefined,
        }}
      >
        <Box
          onClick={(e) => {
            if (item.waived && item.waivedIcon) {
              e.stopPropagation();
              setZoomedWaivedIcon(item.waivedIcon);
            }
          }}
          sx={{
            width: item.waived ? 56 : 32,
            height: item.waived ? 56 : 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: item.waived ? 'transparent' : item.ok ? '#c8e6c9' : '#ffe0b2',
            flexShrink: 0,
            mt: 0.25,
            overflow: item.waived ? 'visible' : 'hidden',
            cursor: item.waived ? 'zoom-in' : 'default',
            transition: 'transform 0.2s ease',
            '&:active': item.waived
              ? { transform: 'scale(1.12)' }
              : undefined,
          }}
        >
          {renderItemIcon(item, item.waived)}
        </Box>
        <Box minWidth={0} flex={1}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: '0.8125rem' }}>
            {item.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: item.waived ? 'text.secondary' : item.ok ? '#2e7d32' : '#ef6c00',
              display: 'block',
              lineHeight: 1.3,
              mt: 0.25,
            }}
          >
            {item.statusText}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderGroup = (group: (typeof CHECKLIST_GROUPS)[number]) => {
    const groupItems = group.keys.map((key) => itemsByKey[key]).filter(Boolean) as ChecklistItem[];
    if (groupItems.length === 0) return null;

    const useGrid = group.columns === 2;

    return (
      <Box key={group.title} mb={1.5}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
            display: 'block',
            mb: 0.75,
          }}
        >
          {group.title}
        </Typography>
        {useGrid ? (
          <Grid container spacing={0.75}>
            {groupItems.map((item) => (
              <Grid key={item.key} size={{ xs: 12, sm: 6 }}>
                {renderChecklistItem(item)}
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box display="flex" flexDirection="column" gap={0.75}>
            {groupItems.map((item) => renderChecklistItem(item))}
          </Box>
        )}
      </Box>
    );
  };

  const header = (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
      <Typography variant={embedded ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
        Checklist para Caça
      </Typography>
      <Box
        sx={{
          px: 1.25,
          py: 0.4,
          borderRadius: 2,
          bgcolor: checklistOk ? '#e8f5e9' : '#fff3e0',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: checklistOk ? '#2e7d32' : '#ef6c00',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {checklistOk ? 'Pronto' : 'Pendente'}
        </Typography>
      </Box>
    </Box>
  );

  const progress = (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
          {checklistDone} de {checklistTotal} documentos
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: branding.colors.primary, fontSize: '0.8125rem' }}>
          {Math.round(checklistProgress)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={checklistProgress}
        sx={{
          height: 6,
          borderRadius: 3,
          mb: 1.5,
          bgcolor: '#e0e0e0',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            bgcolor: checklistOk ? '#2e7d32' : branding.colors.primary,
          },
        }}
      />
    </>
  );

  const collapsedSummary = checklistOk && !detailsOpen && (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.25,
        borderRadius: 2,
        bgcolor: '#e8f5e9',
        border: '1px solid #c8e6c9',
        mb: 0,
      }}
    >
      <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
          Pronto para caça
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {checklistDone}/{checklistTotal} documentos em dia
        </Typography>
      </Box>
      <Button
        size="small"
        endIcon={<ExpandMore />}
        onClick={() => setDetailsOpen(true)}
        sx={{ flexShrink: 0, textTransform: 'none' }}
      >
        Detalhes
      </Button>
    </Box>
  );

  const details = (
    <Collapse in={!checklistOk || detailsOpen}>
      <Box
        sx={{
          bgcolor: branding.colors.surface,
          borderRadius: 2,
          p: 1.25,
          mb: 1.5,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={isOwner}
              onChange={(e) => handleOwnerChange(e.target.checked)}
              color="success"
              size="small"
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                Sou proprietário da fazenda
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Dispensa autorização da fazenda
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start', m: 0, mb: 1 }}
        />
        <Divider sx={{ mb: 1 }} />
        <FormControlLabel
          control={
            <Switch
              checked={huntWithDogs}
              onChange={(e) => handleHuntWithDogsChange(e.target.checked)}
              color="success"
              size="small"
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                Caça com cães
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Marcado exige carteira de vacinação e laudo veterinário
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start', m: 0 }}
        />
      </Box>

      {CHECKLIST_GROUPS.map((group) => renderGroup(group))}

      {checklistOk && (
        <Button
          size="small"
          startIcon={<ExpandLess />}
          onClick={() => setDetailsOpen(false)}
          sx={{ textTransform: 'none', mt: 0.5 }}
        >
          Recolher checklist
        </Button>
      )}
    </Collapse>
  );

  return (
    <Box>
      {header}
      {progress}
      {collapsedSummary}
      {details}

      <Dialog
        open={zoomedWaivedIcon !== null}
        onClose={() => setZoomedWaivedIcon(null)}
        maxWidth={false}
        slotProps={{
          backdrop: {
            sx: { bgcolor: 'rgba(0, 0, 0, 0.72)' },
            onClick: () => setZoomedWaivedIcon(null),
          },
        }}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            m: 2,
          },
        }}
      >
        <Zoom in={zoomedWaivedIcon !== null} timeout={220}>
          <Box
            onClick={() => setZoomedWaivedIcon(null)}
            sx={{
              cursor: 'zoom-out',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              outline: 'none',
            }}
          >
            {zoomedWaivedIcon === 'dog' ? (
              <DogChecklistIcon size={280} />
            ) : zoomedWaivedIcon === 'farm' ? (
              <RusticFarmIcon size={280} />
            ) : null}
          </Box>
        </Zoom>
      </Dialog>
    </Box>
  );
};

export default DocumentChecklist;
